<template>
  <v-select
    v-model="model"
    :placeholder="placeholder || ''"
    :clearable="clearable"
    :error-messages="errors"
    :messages="messages"
    :multiple="multiple"
    :disabled="disabled"
    :readonly="readonly"
    :dark="darkEnabled"
    :items="options"
    :dense="dense"
    :label="label"
    :solo="solo"
    :hint="hint"
    outlined
    rounded
    flat
    :menu-props="menuProps"
    @input="onChange"
    @blur="onBlur"
  >
    <template v-slot:append>
      <slot name="append">
        <v-tooltip top v-if="help">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">far fa-question-circle</v-icon>
          </template>
          {{ help }}
        </v-tooltip>
      </slot>
    </template>
    <template v-slot:selection="{ item, index }">
      <template v-if="isMultiCondensed">
        <div v-if="index === 0" class="card card--multi-summary">
          {{ selectionSummaryText }}
        </div>
      </template>
      <template v-else>
        <div v-if="item.icon">
          <i :class="item.icon"></i>
        </div>
        <div
          v-else
          class="card"
          :class="{ 'card--inactive': isOptionDisabled(item) }"
        >
          {{ item.text }}
        </div>
      </template>
    </template>
    <template v-slot:item="{ item }">
      <slot name="option" :option="item">
        <div
          v-if="item.icon"
          class="form-select-option"
          :class="{ 'form-select-option--inactive': isOptionDisabled(item) }"
        >
          <i :class="item.icon"></i>
          <span class="form-select-option-hint">{{ item.text }}</span>
        </div>
        <div
          v-else
          class="form-select-option"
          :class="{ 'form-select-option--inactive': isOptionDisabled(item) }"
        >
          {{ item.text }}
        </div>
      </slot>
    </template>
  </v-select>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "FormSelect",
  props: {
    options: {
      type: Array,
      default: () => [],
      required: false
    },
    value: {
      type: [Array, String, Object],
      default: "",
      required: false
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    hint: {
      type: String,
      default: "",
      required: false
    },
    placeholder: {
      type: String,
      default: "",
      required: false
    },
    dense: {
      type: Boolean,
      default: true,
      required: false
    },
    clearable: {
      type: Boolean,
      default: false,
      required: false
    },
    multiple: {
      type: Boolean,
      default: false,
      required: false
    },
    solo: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    messages: {
      type: String,
      default: "",
      required: false
    },
    errorMessage: {
      type: [Object],
      default: () => ({}),
      required: false
    },
    validation: {
      type: Object,
      default: () => null,
      required: false
    },
    help: {
      type: String,
      default: "",
      required: false
    }
  },
  data: () => ({
    visibled: false,
    model: "",
    touched: false
  }),
  watch: {
    value: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal != oldVal && newVal != this.model) {
          this.model = newVal;
        }
      }
    },
    validation: {
      deep: true,
      handler(newVal) {
        this.touched = newVal.$dirty;
      }
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    menuProps() {
      const contentClass = this.darkEnabled
        ? "form-select-menu form-select-menu-dark"
        : "form-select-menu";
      return { contentClass };
    },
    isMultiCondensed() {
      return (
        this.multiple && Array.isArray(this.model) && this.model.length > 2
      );
    },
    selectionSummaryText() {
      if (!this.isMultiCondensed) {
        return "";
      }
      const labels = this.model.slice(0, 2).map(v => {
        const opt = this.options.find(o => o && o.value === v);
        return opt && opt.text != null ? opt.text : String(v);
      });
      const extra = this.model.length - 2;
      const suffix = extra === 1 ? "+1 item" : `+${extra} itens`;
      return `${labels[0]}, ${labels[1]}, ${suffix}`;
    },
    errors() {
      if (this.validation && (this.validation.$dirty || this.touched)) {
        let errorsMess = [];
        if (!this.validation.required) {
          errorsMess.push(this.errorMessage["required"] || "Campo obrigatório");
        }
        return errorsMess;
      }
      return [];
    }
  },
  created() {
    this.model = this.value;
  },
  methods: {
    onChange() {
      this.$emit("change", this.model);
      this.$emit("input", this.model);
      this.checkValidation();
    },
    onBlur() {
      this.$emit("blur", this.model);
      this.checkValidation();
    },
    checkValidation() {
      if (this.validation) {
        this.validation.$touch();
        this.touched = true;
      }
    },
    isOptionDisabled(item) {
      if (!item) return false;
      if (typeof item === "object" && item.disabled === true) return true;
      const value = typeof item === "object" ? item.value : item;
      const opt = this.options.find(o => o && o.value === value);
      return !!(opt && opt.disabled === true);
    }
  }
};
</script>

<style scoped>
.card {
  display: flex;
  align-items: center;
  background-color: #e0e0e0;
  color: rgba(0, 0, 0, 0.87) !important;
  font-size: 13px;
  height: 24px;
  margin: 4px;
  padding: 0 12px;
  border-radius: 16px;
}

.card--multi-summary {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  line-height: 24px;
}

.card--inactive {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}
</style>

<style>
.form-select-option {
  width: 100%;
}

.v-menu__content.form-select-menu-dark {
  background: var(--dark-surface, #1d1d1d) !important;
}

.v-menu__content.form-select-menu-dark .v-list {
  background: var(--dark-surface, #1d1d1d) !important;
}

.v-menu__content.form-select-menu-dark .v-select-list.v-card {
  background: var(--dark-surface, #1d1d1d) !important;
  box-shadow: none !important;
}

.v-menu__content.form-select-menu-dark
  .v-list
  .v-list-item:not(.v-list-item--disabled) {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-select-menu-dark .v-list-item__title,
.v-menu__content.form-select-menu-dark .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-select-menu-dark .v-list-item__content,
.v-menu__content.form-select-menu-dark .v-list-item__content p,
.v-menu__content.form-select-menu-dark .v-list-item__content small,
.v-menu__content.form-select-menu-dark
  .v-list-item:not(.v-list-item--disabled)
  > div,
.v-menu__content.form-select-menu-dark
  .v-list-item:not(.v-list-item--disabled)
  p,
.v-menu__content.form-select-menu-dark
  .v-list-item:not(.v-list-item--disabled)
  small {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-select-menu-dark .v-divider {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.v-menu__content.form-select-menu-dark input {
  color: rgba(255, 255, 255, 0.87) !important;
  caret-color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-select-menu-dark
  .v-list-item:not(.v-list-item--disabled)
  .v-simple-checkbox
  .v-icon {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-select-menu-dark .form-select-option-hint {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 9px;
  padding-left: 5px;
}

/* disabled/inactive — must stay after generic menu rules */
.v-menu__content.form-select-menu .v-list-item--disabled {
  opacity: 1 !important;
  cursor: default !important;
}

.v-menu__content.form-select-menu .v-list-item--disabled .v-list-item__content,
.v-menu__content.form-select-menu .v-list-item--disabled > div,
.v-menu__content.form-select-menu .form-select-option--inactive {
  color: #9e9e9e !important;
}

.v-menu__content.form-select-menu
  .v-list-item--disabled
  .v-simple-checkbox
  .v-icon {
  color: #bdbdbd !important;
}

.v-menu__content.form-select-menu .v-list-item--disabled.v-list-item--active,
.v-menu__content.form-select-menu
  .v-list-item--disabled.v-list-item--highlight {
  background-color: transparent !important;
}

.v-menu__content.form-select-menu
  .v-list-item--disabled.v-list-item--active::before,
.v-menu__content.form-select-menu
  .v-list-item--disabled.v-list-item--highlight::before {
  background-color: var(--primary-color, #12558c) !important;
  opacity: 0.12 !important;
}

.v-menu__content.form-select-menu-dark .v-list-item--disabled {
  opacity: 1 !important;
  cursor: default !important;
}

.v-menu__content.form-select-menu-dark
  .v-list-item--disabled
  .v-list-item__content,
.v-menu__content.form-select-menu-dark .v-list-item--disabled > div,
.v-menu__content.form-select-menu-dark .form-select-option--inactive {
  color: rgba(255, 255, 255, 0.38) !important;
}

.v-menu__content.form-select-menu-dark
  .v-list-item--disabled
  .v-simple-checkbox
  .v-icon {
  color: rgba(255, 255, 255, 0.38) !important;
}

.v-menu__content.form-select-menu-dark
  .v-list-item--disabled.v-list-item--active,
.v-menu__content.form-select-menu-dark
  .v-list-item--disabled.v-list-item--highlight {
  background-color: transparent !important;
}

.v-menu__content.form-select-menu-dark
  .v-list-item--disabled.v-list-item--active::before,
.v-menu__content.form-select-menu-dark
  .v-list-item--disabled.v-list-item--highlight::before {
  background-color: var(--primary-color, #64b5f6) !important;
  opacity: 0.12 !important;
}
</style>
